import { Component, Input, OnDestroy } from '@angular/core';
import { Address, Contact, ProviderInfo, ServiceProviderInfo, Trade } from '../../models/service-provider-info-model';
import { ServiceProviderService } from '../../services/service-provider.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-ipex-service-provider',
    templateUrl: './ipex-service-provider.component.html',
    styleUrls: ['./ipex-service-provider.component.scss'],
    standalone: false
})
export class IpexServiceProviderComponent implements OnDestroy {
    providerInfo: ProviderInfo;

    @Input() set serviceProviderId(value: string) {
        this.getServiceProviderInfo(value);
    }

    @Input() addressId: string = '';
    @Input() contactId: string = '';
    @Input() caseOrigin: string = '';
    @Input() claimOrigin: string = '';
    @Input() caseId: string = '';

    @Input() tradeList: Trade[] = [];

    private readonly destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(private readonly serviceProviderService: ServiceProviderService) {}

    private getServiceProviderInfo(serviceProviderItemId: string) {
        if (!serviceProviderItemId) {
            return;
        }
        this.serviceProviderService
            .getServiceProviderDetail(serviceProviderItemId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: response => {
                    this.createServiceProviderInfo(response);
                },
                error: err => {
                    console.error(err);
                }
            });
    }

    private createServiceProviderInfo(serviceProviderInfo: ServiceProviderInfo): void {
        const { serviceProvider, auditCases, repairCases } = serviceProviderInfo;
        this.providerInfo = new ProviderInfo();
        this.providerInfo.name = serviceProvider?.name ?? '';
        this.providerInfo.uid = serviceProvider?.uid ?? '';

        const address = this.getAddress(this.addressId, serviceProvider?.addresses ?? []);
        this.providerInfo.street = address?.street ?? '';
        this.providerInfo.streetNo = address?.no ?? '';
        this.providerInfo.zip = address?.zip ?? '';
        this.providerInfo.city = address?.city ?? '';
        this.providerInfo.tradeIds = address?.tradeIds ?? [];

        const contact = this.getContact(this.contactId, address?.contacts ?? []);
        this.providerInfo.contactName = contact?.name ?? '';
        this.providerInfo.email = contact?.mail ?? '';
        this.providerInfo.phone = contact?.phone ?? '';

        const componentThis = this;
        this.providerInfo.auditCases =
            auditCases?.results
                ?.filter(item => item.caseId !== this.caseId)
                .map(item => {
                    return componentThis.caseOrigin + item.caseId;
                }) ?? [];

        this.providerInfo.repairCases =
            repairCases?.results
                ?.filter(item => item.caseId !== this.caseId)
                .map(item => {
                    return componentThis.claimOrigin + item.caseId;
                }) ?? [];
        this.providerInfo.auditCasesCount = auditCases?.totalCount ?? 0;
        this.providerInfo.repairCasesCount = this.providerInfo.repairCases.length;
    }

    private getAddress(addressId: string, addressList: Address[]): Address | null {
        const address = addressList?.find(item => item.id === addressId);
        if (!address) {
            return null;
        }

        return address;
    }

    private getContact(contactId: string, contactList: Contact[]): Contact | null {
        const contact = contactList?.find(item => item.id === contactId);
        if (!contact) {
            return null;
        }
        return contact;
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
}
